import React, { useEffect, useMemo, useState } from "react";
import { db } from "../services/firebase";
import { ref, onValue, update } from "firebase/database";

import {
  FaUser,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaUniversity,
  FaIdCard,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSearch,
  FaLayerGroup,
} from "react-icons/fa";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AdminTeacherApproval.css";

function AdminTeacherApproval() {
  const [teachers, setTeachers] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const tRef = ref(db, "teacherRegistrations");
    onValue(tRef, (snap) => {
      setTeachers(snap.exists() ? snap.val() : {});
    });
  }, []);

  const updateStatus = async (uid, status) => {
    try {
      await update(ref(db, "teacherRegistrations/" + uid), {
        status,
        reviewedAt: new Date().toISOString(),
      });
      toast.success(`Teacher ${status.toUpperCase()}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const list = Object.entries(teachers);

  const stats = {
    total: list.length,
    pending: list.filter(([, t]) => t.status === "pending").length,
    approved: list.filter(([, t]) => t.status === "approved").length,
    rejected: list.filter(([, t]) => t.status === "rejected").length,
  };

  const filtered = useMemo(() => {
    return list.filter(([, t]) => {
      const matchesFilter = filter === "all" || t.status === filter;
      const q = search.toLowerCase();
      const matchesSearch =
        t.firstName?.toLowerCase().includes(q) ||
        t.lastName?.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q) ||
        t.college?.toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });
  }, [list, filter, search]);

  return (
    <div className="ta-wrapper">
      <ToastContainer position="top-right" />

      {/* Header */}
      <div className="ta-header">
        <h1>🎓 Teacher Approval</h1>
        <p>Review and manage teacher registration requests for BodhAI.</p>
      </div>

      {/* Stats */}
      <div className="ta-stats">
        <Stat label="Total Registrations" value={stats.total} icon={<FaUsers />} />
        <Stat label="Pending Review" value={stats.pending} type="pending" icon={<FaClock />} />
        <Stat label="Approved" value={stats.approved} type="approved" icon={<FaCheckCircle />} />
        <Stat label="Rejected" value={stats.rejected} type="rejected" icon={<FaTimesCircle />} />
      </div>

      {/* Filters */}
      <div className="ta-toolbar">
        <div className="ta-tabs">
          <Tab label="All" count={stats.total} icon={<FaLayerGroup />} active={filter==="all"} onClick={()=>setFilter("all")} />
          <Tab label="Pending" count={stats.pending} icon={<FaClock />} active={filter==="pending"} onClick={()=>setFilter("pending")} />
          <Tab label="Approved" count={stats.approved} icon={<FaCheckCircle />} active={filter==="approved"} onClick={()=>setFilter("approved")} />
          <Tab label="Rejected" count={stats.rejected} icon={<FaTimesCircle />} active={filter==="rejected"} onClick={()=>setFilter("rejected")} />
        </div>

        <div className="ta-search">
          <FaSearch />
          <input
            placeholder="Search teachers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Cards */}
      <div className="ta-grid">
        {filtered.map(([uid, t]) => (
          <div className="ta-card" key={uid}>
            <div className="ta-card-header">
              <div className="ta-avatar">
                <FaUser />
              </div>
              <div>
                <h3>{t.firstName} {t.lastName}</h3>
                <span className={`ta-badge ${t.status}`}>
                  {t.status === "pending" && <FaClock />}
                  {t.status === "approved" && <FaCheckCircle />}
                  {t.status === "rejected" && <FaTimesCircle />}
                  {t.status}
                </span>
              </div>
            </div>

            <div className="ta-info">
              <p><FaEnvelope /> {t.email}</p>
              <p><FaPhone /> {t.phone}</p>
              <p><FaUniversity /> {t.college}</p>
              <p><FaIdCard /> {t.designation}</p>
            </div>

            <div className="ta-message">
              “{t.message || "No message provided"}”
            </div>

            <div className="ta-docs">
              {t.idCardLink && (
                <a href={t.idCardLink} target="_blank" rel="noreferrer">
                  <FaIdCard /> ID Card
                </a>
              )}
              {t.aadharCardLink && (
                <a href={t.aadharCardLink} target="_blank" rel="noreferrer">
                  <FaIdCard /> Aadhaar Card
                </a>
              )}
            </div>

            <div className="ta-footer">
              <span>Registered:  {t.registrationDate || "N/A"}</span>

              {t.status === "pending" && (
                <div className="ta-actions">
                  <button className="approve" onClick={() => updateStatus(uid, "approved")}>
                    <FaCheckCircle /> Approve
                  </button>
                  <button className="reject" onClick={() => updateStatus(uid, "rejected")}>
                    <FaTimesCircle /> Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Small Components ---------------- */

const Stat = ({ label, value, type, icon }) => (
  <div className={`ta-stat ${type || ""}`}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <p>{label}</p>
      <span style={{ fontSize: 22 }}>{icon}</span>
    </div>
    <h2>{value}</h2>
  </div>
);

const Tab = ({ label, count, active, onClick, icon }) => (
  <button className={`ta-tab ${active ? "active" : ""}`} onClick={onClick}>
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {icon}
      {label}
    </span>
    <span>{count}</span>
  </button>
);

export default AdminTeacherApproval;
